// 設定の画面（仕様書「設定画面からの数値の変更、機能の ON / OFF」）。直せるのはシステム管理者だけ。法令で決まった値は出すだけ
import { useEffect, useState } from 'react';
import { FEATURE_DEFS, RULES, SETTING_DEFS, SettingsUpdate, can, type FeatureKey, type SettingKey } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { useMe } from '../lib/me';
import { useSettings } from '../lib/settings';
import { useStepUp } from './StepUp';
import { useToast } from './ui';

export function SettingsPanel() {
  const me = useMe();
  const toast = useToast();
  const { ensureRecent } = useStepUp();
  const { settings, loaded, reload } = useSettings();
  const [values, setValues] = useState<Record<string, string>>({});
  const [features, setFeatures] = useState<Record<FeatureKey, boolean>>(settings.features);
  const [err, setErr] = useState('');
  // 読み直しても版が同じなら、入力中の値を上書きしない（つながり直しの読み直しで入力が消えないように）
  // 入れ終わるまでは入力欄を押せなくする（読み込んだ直後に入れた値が、あとから上書きされないように）
  const [applied, setApplied] = useState<number | null>(null);
  useEffect(() => {
    if (!loaded || applied === settings.version) return;
    setApplied(settings.version);
    setValues(Object.fromEntries(Object.entries(settings.values).map(([k, v]) => [k, String(v)]))); setFeatures(settings.features);
  }, [settings, loaded]);
  const ready = loaded && applied !== null;
  const canEdit = !!me && can(me.role, 'manageStaff');
  const save = async () => {
    setErr('');
    const r = SettingsUpdate.safeParse({ values: Object.fromEntries(SETTING_DEFS.map((d) => [d.key, Number(values[d.key])])), features });
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    if (!(await ensureRecent('設定の変更'))) return;
    try { await api.patch('/v1/office/settings', r.data, { ifMatch: settings.version }); toast('設定を保存しました'); void reload(); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '保存できませんでした'); }
  };
  return (
    <section className="panel" aria-label="設定" style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div><b>設定</b><br /><small className="muted">事務所の運用で決める数値と、機能の ON / OFF。{canEdit ? '' : '直せるのはシステム管理者だけです'}</small></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
        {SETTING_DEFS.map((d) => (
          <div className="field" key={d.key}><label htmlFor={`st-${d.key}`}>{d.label}（{d.min}〜{d.max}{d.unit}・既定 {d.def}）</label>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input id={`st-${d.key}`} inputMode="numeric" disabled={!canEdit || !ready} value={values[d.key as SettingKey] ?? ''} onChange={(e) => setValues({ ...values, [d.key]: e.target.value })} style={{ width: 90 }} />{d.unit}</span></div>
        ))}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {FEATURE_DEFS.map((d) => (
          <label key={d.key} style={{ fontSize: 14 }}><input type="checkbox" style={{ width: 'auto' }} disabled={!canEdit || !ready} checked={features[d.key]} onChange={(e) => setFeatures({ ...features, [d.key]: e.target.checked })} /> {d.label}<small className="muted" style={{ display: 'block', marginLeft: 22 }}>{d.note}</small></label>
        ))}
      </div>
      <small className="muted">法令で決まっていて変えられない値：休息 {RULES.minRestHours} 時間未満は勤務を開始できない・連続運転 {RULES.continuousDriveAlertMin / 60} 時間で警告・1 日の拘束時間 {RULES.dailyRestraintAlertMin / 60} 時間で警告（上限 {RULES.dailyRestraintMaxMin / 60} 時間）</small>
      {err && <p className="err" role="alert">{err}</p>}
      {canEdit && <button className="btn pri" style={{ justifySelf: 'start' }} disabled={!ready} onClick={() => void save()}>設定を保存する</button>}
    </section>
  );
}
